import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  Check,
  FileAudio,
  FileCode2,
  FileText,
  Film,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  LoaderCircle,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { useAuthContext } from './components/AuthProvider';
import {
  addSpaceItems,
  createSpace,
  createSpaceNode,
  createSpaceSkill,
  deleteSpace,
  deleteSpaceNodes,
  duplicateSpaceNodes,
  getCharacters,
  getLibraryCharacters,
  getOrgAssets,
  getSpace,
  getSpaceNodeAncestors,
  getSpaceNodes,
  listImageLibrary,
  listVideoLibrary,
  moveSpaceNodes,
  previewSpaceNodeDeletion,
  removeSpaceItem,
  searchSpaceNodes,
  updateSpace,
  updateSpaceItem,
  uploadSpaceItem,
} from './lib/api';
import {
  fetchSpacesIfStale,
  useCharacterDetailsStore,
  useCharactersStore,
  useOrgAssetDetailsStore,
  useOrgAssetsStore,
  useSpacesData,
  useSpacesStore,
} from './stores';
import './SpacesPage.css';

const FILTERS = [
  ['all', 'All'],
  ['folder', 'Folders'],
  ['text', 'Text'],
  ['skill', 'Skills'],
  ['image', 'Images'],
  ['video', 'Videos'],
  ['audio', 'Audio'],
  ['character', 'Characters'],
];

const SPACE_FILE_EXTENSION = /\.(?:avif|gif|jpe?g|png|svg|webp|m4v|mkv|mov|mp4|webm|aac|flac|m4a|mp3|ogg|wav|mdx?|txt|json|ya?ml|csv|html?|css|[cm]?js|tsx?|jsx|xml)$/i;

function markdownInline(text) {
  const token = /(\[[^\]]+\]\(https?:\/\/[^)]+\)|\*\*[^*]+\*\*)/g;
  return String(text).split(token).filter(Boolean).map((part, index) => {
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
    if (link) return <a key={index} href={link[2]} target="_blank" rel="noreferrer noopener">{link[1]}</a>;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    return part;
  });
}

function SafeMarkdownPreview({ content }) {
  const blocks = [];
  const lines = String(content || '').split('\n');
  let code = null;
  lines.forEach((line, index) => {
    if (line.startsWith('```')) {
      if (code) { blocks.push(<pre key={`code-${index}`}><code>{code.lines.join('\n')}</code></pre>); code = null; }
      else code = { lines: [] };
      return;
    }
    if (code) { code.lines.push(line); return; }
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) { const Tag = `h${heading[1].length}`; blocks.push(<Tag key={index}>{markdownInline(heading[2])}</Tag>); return; }
    const list = line.match(/^\s*[-*]\s+(.*)$/);
    if (list) { blocks.push(<div className="space-markdown-list" key={index}>• {markdownInline(list[1])}</div>); return; }
    if (line.trim()) blocks.push(<p key={index}>{markdownInline(line)}</p>);
  });
  if (code) blocks.push(<pre key="code-final"><code>{code.lines.join('\n')}</code></pre>);
  return blocks.length ? blocks : <p>This file is empty.</p>;
}

function MediaGlyph({ type, size = 22 }) {
  if (type === 'folder') return <Folder size={size} />;
  if (type === 'text') return <FileText size={size} />;
  if (type === 'skill') return <FileCode2 size={size} />;
  if (type === 'video') return <Film size={size} />;
  if (type === 'audio') return <FileAudio size={size} />;
  if (type === 'character') return <Users size={size} />;
  return <ImageIcon size={size} />;
}

function MediaPreview({ item, className = '' }) {
  const type = item.mediaType || item.type || 'image';
  const url = item.thumbnailUrl || item.imageUrl || item.assetImageUrl || (type === 'image' ? item.mediaUrl : null);
  if (url) return <img className={className} src={url} alt="" />;
  if (type === 'video' && (item.mediaUrl || item.videoUrl)) {
    return <video className={className} src={item.mediaUrl || item.videoUrl} muted playsInline preload="metadata" />;
  }
  return <div className={`${className} spaces-media-placeholder`}><MediaGlyph type={type} /></div>;
}

function SpaceItemCard({ item, onOpen, onRemove, selected = false, onToggle }) {
  const isFolder = item.kind === 'folder' || item.mediaType === 'folder';
  return (
    <article className="space-item-card">
      <button className={`space-item-select${selected ? ' selected' : ''}`} onClick={() => onToggle(item)} aria-label={`${selected ? 'Deselect' : 'Select'} ${item.name}`} aria-pressed={selected}>{selected ? <Check size={13} /> : <span />}</button>
      <button className={`space-item-open${isFolder ? ' is-folder' : ''}`} onClick={() => onOpen(item)} aria-label={`Open ${item.name}`}>
        <div className="space-item-media">
          <MediaPreview item={item} className="spaces-item-image" />
          <span className="space-item-type"><MediaGlyph type={item.mediaType} size={12} /> {item.mediaType}</span>
        </div>
        <strong>{item.name}</strong>
        <span className="space-item-description">
          <span>{isFolder ? `${item.childCount || 0} items` : item.description || 'No description'}</span>
          {!isFolder && item.descriptionEditable !== false && <Pencil size={12} />}
        </span>
      </button>
      <button
        className="space-item-remove"
        onClick={() => onRemove(item)}
        aria-label={`Remove ${item.name} from Space`}
        title="Remove from Space"
      >
        <MoreHorizontal size={17} />
      </button>
    </article>
  );
}

function SpaceMoveDialog({ open, spaceId, selectedIds, onClose, onMoved }) {
  const [folders, setFolders] = useState([]);
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true); setError(''); setTarget('');
    const load = async () => {
      const results = [];
      const queue = [{ id: null, path: '' }];
      while (queue.length) {
        const parent = queue.shift();
        let cursor = null;
        do {
          const response = await getSpaceNodes(spaceId, { parentId: parent.id, limit: 100, cursor });
          (response.items || []).filter((item) => item.kind === 'folder').forEach((folder) => {
            const path = parent.path ? `${parent.path} / ${folder.name}` : folder.name;
            results.push({ ...folder, path });
            queue.push({ id: folder.id, path });
          });
          cursor = response.nextCursor || null;
        } while (cursor);
      }
      if (active) setFolders(results);
    };
    load().catch((err) => active && setError('Could not load folders')).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, spaceId]);

  if (!open) return null;
  const submit = async (event) => {
    event.preventDefault(); setLoading(true); setError('');
    try { await moveSpaceNodes(spaceId, selectedIds, target || null); onMoved(); onClose(); }
    catch (err) { setError('Move failed'); }
    finally { setLoading(false); }
  };
  return <div className="spaces-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><form className="spaces-dialog" role="dialog" aria-modal="true" aria-label="Move selected items" onSubmit={submit}><header className="spaces-dialog-header"><div><h2>Move {selectedIds.length} items</h2><p>Choose a destination in this Space.</p></div><button type="button" className="spaces-icon-button" onClick={onClose}><X size={17} /></button></header><label>Destination<select value={target} onChange={(event) => setTarget(event.target.value)}><option value="">Space root</option>{folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.path}</option>)}</select></label>{error && <div className="spaces-error">{error}</div>}<footer className="spaces-dialog-actions"><button type="button" className="spaces-button secondary" onClick={onClose}>Cancel</button><button className="spaces-button primary" disabled={loading}>{loading ? 'Moving…' : 'Move'}</button></footer></form></div>;
}

function ExpandedSpaceItemModal({ item, onClose, onDescriptionSaved, onNameSaved, onRemove }) {
  const [name, setName] = useState(item.name || '');
  const [editingName, setEditingName] = useState(false);
  const [description, setDescription] = useState(item.description || '');
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [removing, setRemoving] = useState(false);
  const editable = item.descriptionEditable !== false;
  const type = item.mediaType || 'image';
  const mediaUrl = item.mediaUrl || item.videoUrl || item.audioUrl || item.thumbnailUrl || item.imageUrl || item.assetImageUrl;

  useEffect(() => {
    setName(item.name || '');
    setDescription(item.description || '');
  }, [item.description, item.id, item.name]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving && !savingName && !removing) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, removing, saving, savingName]);

  const save = async () => {
    if (saving || description.trim() === (item.description || '')) return;
    setSaving(true);
    try {
      await onDescriptionSaved(item, description);
    } finally {
      setSaving(false);
    }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (savingName || saving || !trimmed || trimmed === item.name) return;
    setSavingName(true);
    try {
      await onNameSaved(item, trimmed);
      setEditingName(false);
    } finally {
      setSavingName(false);
    }
  };

  const cancelNameEdit = () => {
    setName(item.name || '');
    setEditingName(false);
  };

  const remove = async () => {
    if (saving || removing) return;
    setRemoving(true);
    const removed = await onRemove(item);
    if (!removed) setRemoving(false);
  };

  return (
    <div className="spaces-modal-backdrop space-element-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <section className="space-element-modal" role="dialog" aria-modal="true" aria-label={item.name}>
        <div className="space-element-preview">
          {type === 'video' && mediaUrl ? (
            <video src={mediaUrl} controls playsInline autoPlay />
          ) : type === 'audio' && mediaUrl ? (
            <div className="space-element-audio">
              <FileAudio size={72} strokeWidth={1.2} />
              <audio src={mediaUrl} controls autoPlay />
            </div>
          ) : mediaUrl ? (
            <img src={mediaUrl} alt={item.name} />
          ) : (
            <div className="space-element-placeholder"><MediaGlyph type={type} size={64} /></div>
          )}
        </div>
        <aside className="space-element-sidebar">
          <header>
            <div>
              <span className="space-element-kind"><MediaGlyph type={type} size={13} /> {type}</span>
              {editable && editingName ? (
                <div className="space-element-title-editor">
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={500}
                    aria-label={`${item.name} name`}
                    autoFocus
                    onFocus={(event) => event.currentTarget.select()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveName();
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelNameEdit();
                      }
                    }}
                  />
                  <button className="space-element-title-action save" onClick={saveName} disabled={savingName || !name.trim() || name.trim() === item.name} aria-label="Save name">
                    {savingName ? <LoaderCircle className="spaces-spin" size={14} /> : <Check size={14} />}
                  </button>
                  <button className="space-element-title-action" onClick={cancelNameEdit} disabled={savingName} aria-label="Cancel rename"><X size={14} /></button>
                </div>
              ) : (
                <h2
                  id="space-element-title"
                  className={editable ? 'is-editable' : ''}
                  onDoubleClick={editable ? () => setEditingName(true) : undefined}
                  title={editable ? 'Double-click to rename' : undefined}
                >
                  {item.name}{editable && <Pencil size={13} aria-hidden="true" />}
                </h2>
              )}
            </div>
            <button className="spaces-icon-button" onClick={onClose} disabled={saving || savingName || removing} aria-label="Close element"><X size={17} /></button>
          </header>
          <div className="space-element-description">
            <div className="space-element-description-heading">
              <label htmlFor="space-element-description">Description</label>
              {editable && <Pencil size={13} />}
            </div>
            {editable ? (
              <>
                <textarea
                  id="space-element-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  maxLength={5000}
                  placeholder="Add a description…"
                  aria-label={`${item.name} description`}
                />
                <div className="space-element-description-actions">
                  <button className="spaces-button secondary" onClick={() => setDescription(item.description || '')} disabled={saving || description === (item.description || '')}>Reset</button>
                  <button className="spaces-button primary" onClick={save} disabled={saving || description.trim() === (item.description || '')}>{saving ? 'Saving…' : 'Save description'}</button>
                </div>
              </>
            ) : (
              <p>{item.description || 'No description.'}</p>
            )}
          </div>
          <footer className="space-element-sidebar-footer">
            <button className="spaces-button danger" onClick={remove} disabled={saving || savingName || removing}>
              <Trash2 size={14} /> {removing ? 'Removing…' : 'Remove from Space'}
            </button>
          </footer>
        </aside>
      </section>
    </div>
  );
}

function CreateSpaceDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);
  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await createSpace({ name: name.trim(), description: description.trim() || null });
      onCreated(response.space);
      setName('');
      setDescription('');
    } catch (err) {
      setError('Could not create Space');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="spaces-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="spaces-dialog" role="dialog" aria-modal="true" aria-labelledby="create-space-title" onSubmit={submit}>
        <div className="spaces-dialog-header">
          <div><h2 id="create-space-title">New Space</h2><p>Group reusable elements around a shared theme.</p></div>
          <button type="button" className="spaces-icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        <label>Name<input ref={inputRef} value={name} onChange={(e) => setName(e.target.value)} maxLength={500} placeholder="Summer campaign" /></label>
        <label>Description <span>Optional</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} placeholder="Visual direction, recurring cast, and audio palette" /></label>
        {error && <div className="spaces-error">{error}</div>}
        <div className="spaces-dialog-actions">
          <button type="button" className="spaces-button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="spaces-button primary" disabled={!name.trim() || saving}>{saving ? 'Creating…' : 'Create Space'}</button>
        </div>
      </form>
    </div>
  );
}

function CreateSpaceNodeDialog({ open, spaceId, parentId, initialType = 'folder', onClose, onCreated }) {
  const [type, setType] = useState(initialType);
  const [name, setName] = useState('');
  const [textFormat, setTextFormat] = useState('plain');
  const [content, setContent] = useState('');
  const [skillDescription, setSkillDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setName('');
    setTextFormat(initialType === 'text' ? 'plain' : 'plain');
    setContent('');
    setSkillDescription('');
    setError('');
  }, [initialType, open]);

  if (!open) return null;
  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true); setError('');
    try {
      if (type === 'skill') {
        const response = await createSpaceSkill(spaceId, {
          name: name.trim(),
          description: skillDescription.trim(),
          instructions: content,
          parentId: parentId || null,
        });
        onCreated(response.folder);
        onClose();
        return;
      }
      const response = await createSpaceNode(spaceId, {
        type, name: name.trim(), parentId: parentId || null,
        ...(type === 'text' ? { content, textFormat } : {}),
      });
      onCreated(response.item);
      onClose();
    } catch (err) {
      setError('Could not create item');
    } finally { setSaving(false); }
  };

  return (
    <div className="spaces-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="spaces-dialog space-node-dialog" role="dialog" aria-modal="true" aria-label={`New ${type}`} onSubmit={submit}>
        <header className="spaces-dialog-header"><div><h2>New Space item</h2><p>Create a folder, document, or reusable skill file.</p></div><button type="button" className="spaces-icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button></header>
        <label>Type<select value={type} onChange={(event) => setType(event.target.value)}><option value="folder">Folder</option><option value="text">Text file</option><option value="skill">Skill</option></select></label>
        <label>Name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={type === 'folder' ? 'Campaign references' : type === 'skill' ? 'social-ad-localizer' : 'Brief.md'} /></label>
        {type === 'skill' && <label>Description<input value={skillDescription} onChange={(event) => setSkillDescription(event.target.value)} maxLength={500} placeholder="Explain when Composer should use this skill" /></label>}
        {type === 'text' && <>
          <label>Format<select value={textFormat} onChange={(event) => setTextFormat(event.target.value)}><option value="plain">Plain text</option><option value="markdown">Markdown</option></select></label>
          <label>Content<textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="Write or paste text…" /></label>
        </>}
        {type === 'skill' && <label>Starter instructions <span>Optional</span><textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="Describe the workflow Composer should follow…" /></label>}
        {error && <div className="spaces-error">{error}</div>}
        <footer className="spaces-dialog-actions"><button type="button" className="spaces-button secondary" onClick={onClose}>Cancel</button><button className="spaces-button primary" disabled={!name.trim() || (type === 'skill' && !skillDescription.trim()) || saving}>{saving ? 'Creating…' : 'Create'}</button></footer>
      </form>
    </div>
  );
}

function TextSpaceItemModal({ item, onClose, onSaved, onRemove }) {
  const [name, setName] = useState(item.name || '');
  const [content, setContent] = useState(item.content || '');
  const [textFormat, setTextFormat] = useState(item.textFormat || 'plain');
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('Saved');
  const [mode, setMode] = useState('edit');
  const [fileSearch, setFileSearch] = useState('');
  const isMarkdown = textFormat === 'markdown' || textFormat === 'skill' || /\.mdx?$/i.test(name);
  const dirty = name.trim() !== item.name || content !== (item.content || '') || textFormat !== (item.textFormat || 'plain');
  const validationMessage = useMemo(() => {
    const extension = name.split('.').pop()?.toLowerCase();
    if (extension === 'json') {
      try { JSON.parse(content || 'null'); return ''; }
      catch (err) { return 'JSON is not valid. Check the formatting and try again.'; }
    }
    if (['yaml', 'yml'].includes(extension)) {
      if (/\t/.test(content)) return 'YAML: tabs are not valid indentation.';
      const badLine = content.split('\n').findIndex((line) => line.trim() && !line.trim().startsWith('#') && !line.includes(':') && !line.trim().startsWith('-'));
      if (badLine >= 0) return `YAML: line ${badLine + 1} may be missing a key separator.`;
    }
    return '';
  }, [content, name]);
  const searchMatches = fileSearch ? content.toLocaleLowerCase().split(fileSearch.toLocaleLowerCase()).length - 1 : 0;
  const save = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setSaveState('Saving…');
    try { await onSaved(item, { name: name.trim(), content: content.replace(/\r\n?/g, '\n'), textFormat }); setSaveState('Saved'); }
    catch { setSaveState('Failed'); }
    finally { setSaving(false); }
  };
  useEffect(() => { if (dirty && saveState === 'Saved') setSaveState('Unsaved'); }, [dirty, saveState]);
  return (
    <div className="spaces-modal-backdrop space-element-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="space-text-modal" role="dialog" aria-modal="true" aria-label={item.name}>
        <header><div className="space-text-title"><MediaGlyph type={item.mediaType} /><input value={name} onChange={(event) => setName(event.target.value)} aria-label={`${item.name} name`} /></div><div>{isMarkdown && <div className="space-text-modes">{['edit', 'split', 'preview'].map((value) => <button key={value} className={mode === value ? 'active' : ''} onClick={() => setMode(value)}>{value}</button>)}</div>}<select value={textFormat} onChange={(event) => { setTextFormat(event.target.value); if (event.target.value === 'plain') setMode('edit'); }} aria-label="Text format"><option value="plain">Plain text</option><option value="markdown">Markdown</option><option value="skill">Skill file</option></select><button className="spaces-icon-button" onClick={onClose} aria-label="Close element"><X size={17} /></button></div></header>
        <div className={`space-text-workspace mode-${isMarkdown ? mode : 'edit'}`}>
          {mode !== 'preview' && <div className="space-text-editor"><pre aria-hidden="true">{content.split('\n').map((_, index) => index + 1).join('\n')}</pre><textarea value={content} onChange={(event) => setContent(event.target.value)} onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') { event.preventDefault(); save(); } }} aria-label={`${item.name} content`} spellCheck="false" /></div>}
          {isMarkdown && mode !== 'edit' && <div className="space-text-preview"><SafeMarkdownPreview content={content} /></div>}
        </div>
        <footer><button className="spaces-button danger" onClick={() => onRemove(item)}><Trash2 size={14} /> Remove</button><label className="space-text-search">Find <input value={fileSearch} onChange={(event) => setFileSearch(event.target.value)} /><small>{fileSearch ? `${searchMatches} matches` : ''}</small></label><span className={saveState === 'Failed' ? 'space-text-failed' : ''}>{validationMessage || saveState}</span><button className="spaces-button secondary" onClick={onClose}>Close</button><button className="spaces-button primary" disabled={saving || !name.trim() || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save'}</button></footer>
      </section>
    </div>
  );
}

export function SpacesIndexPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();
  const { items: spaces, isLoading, error, hasCache } = useSpacesData(user?.id);
  const addCachedSpace = useSpacesStore((state) => state.addSpace);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!loading && user) fetchSpacesIfStale({ userId: user.id });
  }, [loading, user]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return spaces;
    return spaces.filter((space) => `${space.name} ${space.description || ''}`.toLowerCase().includes(term));
  }, [query, spaces]);

  return (
    <AuthenticatedLayout>
      <main className="spaces-page">
        <header className="spaces-page-header">
          <div><h1>Spaces</h1><p>Reusable elements grouped by theme, campaign, or world.</p></div>
          <button className="spaces-button primary" onClick={() => setCreating(true)}><Plus size={16} /> New Space</button>
        </header>
        <div className="spaces-toolbar">
          <label className="spaces-search"><Search size={15} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Spaces…" aria-label="Search Spaces" /></label>
          <span>{visible.length} {visible.length === 1 ? 'Space' : 'Spaces'}</span>
        </div>
        {error && !hasCache && <div className="spaces-error">{error}</div>}
        {isLoading && !hasCache ? <div className="spaces-center"><LoaderCircle className="spaces-spin" /> Loading Spaces…</div> : visible.length ? (
          <div className="spaces-grid">
            {visible.map((space) => (
              <button key={space.id} className="space-card" onClick={() => navigate(`/spaces/${space.id}`)}>
                <div className="space-card-previews">
                  {(space.previewItems || []).slice(0, 4).map((item) => <MediaPreview key={item.id} item={item} className="space-card-preview" />)}
                  {!space.previewItems?.length && <div className="space-card-empty"><FolderOpen size={26} /></div>}
                </div>
                <div className="space-card-copy"><strong>{space.name}</strong><span>{space.itemCount || 0} elements</span></div>
                {space.description && <p>{space.description}</p>}
              </button>
            ))}
          </div>
        ) : (
          <div className="spaces-empty"><FolderOpen size={30} /><h2>{query ? 'No matching Spaces' : 'Create your first Space'}</h2><p>{query ? 'Try another search.' : 'Keep a campaign’s images, videos, audio, and characters together.'}</p>{!query && <button className="spaces-button primary" onClick={() => setCreating(true)}>Create Space</button>}</div>
        )}
      </main>
      <CreateSpaceDialog open={creating} onClose={() => setCreating(false)} onCreated={(space) => { addCachedSpace(space); setCreating(false); navigate(`/spaces/${space.id}`); }} />
    </AuthenticatedLayout>
  );
}

function normalizePickerItems(assets, images, videos, characters) {
  const normalizedAssets = assets.map((asset) => ({
    pickerKey: `asset:${asset.id}`, kind: 'asset', id: asset.id, name: asset.name,
    mediaType: asset.mediaType || 'image', imageUrl: asset.imageUrl,
    videoUrl: asset.videoUrl, audioUrl: asset.audioUrl,
  }));
  const normalizedImages = images.map((image) => ({ pickerKey: `image:${image.id}`, kind: 'image', id: image.id, name: image.prompt || image.projectName || 'Untitled image', mediaType: 'image', imageUrl: image.imageUrl }));
  const normalizedVideos = videos.filter((video) => video.status === 'completed' && video.videoUrl).map((video) => ({ pickerKey: `video:${video.id}`, kind: 'video', id: video.id, name: video.name || video.prompt || video.projectName || 'Untitled video', mediaType: 'video', videoUrl: video.videoUrl }));
  const seen = new Set();
  const normalizedCharacters = characters.filter((character) => character?.id && !seen.has(character.id) && seen.add(character.id)).map((character) => ({ pickerKey: `character:${character.id}`, kind: 'character', id: character.id, name: character.name || 'Untitled character', mediaType: 'character', imageUrl: character.assetImageUrl }));
  return [...normalizedAssets, ...normalizedImages, ...normalizedVideos, ...normalizedCharacters];
}

function SpaceItemPicker({ spaceId, parentId, onClose, onAdded, onUploaded }) {
  const addOrgAsset = useOrgAssetsStore((state) => state.addOrgAsset);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const uploadRef = useRef(null);
  const folderUploadRef = useRef(null);

  useEffect(() => {
    let active = true;
    Promise.all([getOrgAssets(), listImageLibrary({ page: 1, limit: 100 }), listVideoLibrary({ page: 1, limit: 100, search: null }), getCharacters(), getLibraryCharacters()])
      .then(([assets, images, videos, personal, platform]) => {
        if (active) setItems(normalizePickerItems(assets.assets || [], images.images || [], videos.videos || [], [...(personal.characters || []), ...(platform.characters || [])]));
      })
      .catch((err) => active && setError('Could not load library'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const visible = useMemo(() => items.filter((item) => {
    const typeMatch = filter === 'all' || item.mediaType === filter;
    return typeMatch && (!query.trim() || item.name.toLowerCase().includes(query.trim().toLowerCase()));
  }), [filter, items, query]);

  const toggle = (key) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const addSelected = async () => {
    const chosen = items.filter((item) => selected.has(item.pickerKey));
    if (!chosen.length || saving) return;
    setSaving(true); setError(null);
    try {
      const response = await addSpaceItems(spaceId, chosen.map(({ kind, id }) => ({ kind, id, ...(parentId ? { parentId } : {}) })));
      onAdded(response.items || []);
      onClose();
    } catch (err) {
      setError('Could not add elements');
    } finally { setSaving(false); }
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSaving(true); setError(null);
    const supported = files.filter((file) => ['image/', 'video/', 'audio/'].some((prefix) => file.type.startsWith(prefix)) || file.type.startsWith('text/') || SPACE_FILE_EXTENSION.test(file.name));
    const unsupported = files.filter((file) => !supported.includes(file));
    if (unsupported.length && !window.confirm(`${unsupported.length} unsupported file${unsupported.length === 1 ? '' : 's'} will be skipped. Continue with ${supported.length} supported file${supported.length === 1 ? '' : 's'}?`)) {
      setSaving(false); event.target.value = ''; return;
    }
    try {
      const uploaded = [];
      const failures = unsupported.map((file) => `${file.name}: unsupported file type`);
      const folderCache = new Map();
      const childrenCache = new Map();
      const getChildren = async (folderId) => {
        const key = folderId || 'root';
        if (childrenCache.has(key)) return childrenCache.get(key);
        const children = [];
        let cursor = null;
        do {
          const response = await getSpaceNodes(spaceId, { parentId: folderId, cursor, limit: 100 });
          children.push(...(response.items || [])); cursor = response.nextCursor || null;
        } while (cursor);
        childrenCache.set(key, children);
        return children;
      };
      const ensureFolderPath = async (segments) => {
        let currentParent = parentId || null;
        let logicalPath = '';
        for (const segment of segments) {
          logicalPath = logicalPath ? `${logicalPath}/${segment}` : segment;
          if (folderCache.has(logicalPath)) { currentParent = folderCache.get(logicalPath); continue; }
          const children = await getChildren(currentParent);
          let folder = children.find((item) => (item.kind === 'folder' || item.mediaType === 'folder') && item.name.localeCompare(segment, undefined, { sensitivity: 'accent' }) === 0);
          if (!folder) {
            const response = await createSpaceNode(spaceId, { type: 'folder', name: segment, ...(currentParent ? { parentId: currentParent } : {}) });
            folder = response.item;
            children.push(folder);
            if ((folder.parentId || null) === (parentId || null)) uploaded.push(folder);
          }
          currentParent = folder.id;
          folderCache.set(logicalPath, currentParent);
        }
        return currentParent;
      };
      for (const file of supported) {
        try {
          const relativeParts = String(file.webkitRelativePath || '').split('/').filter(Boolean);
          const destinationParent = relativeParts.length > 1 ? await ensureFolderPath(relativeParts.slice(0, -1)) : parentId;
          const response = destinationParent ? await uploadSpaceItem(spaceId, file, destinationParent) : await uploadSpaceItem(spaceId, file);
          if (!relativeParts.length || (destinationParent || null) === (parentId || null)) uploaded.push(response.item);
          if (response.asset) addOrgAsset(response.asset);
        } catch (err) {
          failures.push(`${file.webkitRelativePath || file.name}: ${'upload failed'}`);
        }
      }
      onUploaded(uploaded);
      if (failures.length) setError(`Imported ${supported.length - (failures.length - unsupported.length)} of ${files.length} files. ${failures.join(' · ')}`);
      else onClose();
    } catch (err) {
      setError('Upload failed');
    } finally { setSaving(false); event.target.value = ''; }
  };

  return (
    <div className="spaces-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="spaces-picker" role="dialog" aria-modal="true" aria-labelledby="spaces-picker-title">
        <header className="spaces-dialog-header"><div><h2 id="spaces-picker-title">Add elements</h2><p>Select from your library or upload new media.</p></div><button className="spaces-icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button></header>
        <div className="spaces-picker-tools">
          <label className="spaces-search"><Search size={15} /><input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search library…" /></label>
          <button className="spaces-button secondary" onClick={() => uploadRef.current?.click()} disabled={saving}><Upload size={15} /> Upload files</button>
          <button className="spaces-button secondary" onClick={() => folderUploadRef.current?.click()} disabled={saving}><Folder size={15} /> Upload folder</button>
          <input ref={uploadRef} type="file" hidden multiple accept="image/*,video/*,audio/*,text/*,.md,.mdx,.json,.yaml,.yml,.js,.mjs,.cjs,.ts,.tsx,.jsx,.xml" onChange={upload} />
          <input ref={folderUploadRef} type="file" hidden multiple webkitdirectory="" directory="" onChange={upload} />
        </div>
        <div className="spaces-filter-row" role="tablist">{FILTERS.map(([value, label]) => <button key={value} role="tab" aria-selected={filter === value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}</button>)}</div>
        {error && <div className="spaces-error">{error}</div>}
        <div className="spaces-picker-grid">
          {loading ? <div className="spaces-center"><LoaderCircle className="spaces-spin" /> Loading library…</div> : visible.map((item) => {
            const checked = selected.has(item.pickerKey);
            return <button key={item.pickerKey} className={`spaces-picker-item ${checked ? 'selected' : ''}`} onClick={() => toggle(item.pickerKey)} aria-pressed={checked}><div className="spaces-picker-thumb"><MediaPreview item={item} className="spaces-item-image" />{checked && <span><Check size={13} /></span>}</div><strong>{item.name}</strong><small>{item.mediaType}</small></button>;
          })}
          {!loading && !visible.length && <div className="spaces-center">No matching library elements.</div>}
        </div>
        <footer className="spaces-dialog-actions"><span>{selected.size} selected</span><button className="spaces-button secondary" onClick={onClose}>Cancel</button><button className="spaces-button primary" onClick={addSelected} disabled={!selected.size || saving}>{saving ? 'Adding…' : `Add ${selected.size || ''}`}</button></footer>
      </section>
    </div>
  );
}

export function SpaceDetailPage() {
  const { spaceId, folderId } = useParams();
  const navigate = useNavigate();
  const updateCachedSpace = useSpacesStore((state) => state.updateSpace);
  const removeCachedSpace = useSpacesStore((state) => state.removeSpace);
  const addOrgAsset = useOrgAssetsStore((state) => state.addOrgAsset);
  const updateOrgAssetInStore = useOrgAssetsStore((state) => state.updateOrgAsset);
  const patchOrgAssetDetail = useOrgAssetDetailsStore((state) => state.patchEntry);
  const updateCharacterInStore = useCharactersStore((state) => state.updateCharacter);
  const patchCharacterDetail = useCharacterDetailsStore((state) => state.patchEntry);
  const dragDepth = useRef(0);
  const deepLinkNoticeRef = useRef('');
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nodeDialogType, setNodeDialogType] = useState(null);
  const currentFolderId = folderId || null;
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [searchTotal, setSearchTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(deepLinkNoticeRef.current || null);
    deepLinkNoticeRef.current = '';
    const childrenRequest = typeof getSpaceNodes === 'function'
      ? getSpaceNodes(spaceId, { parentId: currentFolderId })
      : Promise.resolve(null);
    const ancestorsRequest = currentFolderId && typeof getSpaceNodeAncestors === 'function'
      ? getSpaceNodeAncestors(spaceId, currentFolderId)
      : Promise.resolve({ ancestors: [] });
    Promise.all([
      getSpace(spaceId),
      childrenRequest,
      ancestorsRequest,
    ]).then(([response, children, ancestorResponse]) => {
      if (!active) return;
      setSpace({ ...response.space, items: children?.items || response.space.items || [] });
      setNextCursor(children?.nextCursor || null);
      setBreadcrumbs([
        ...(ancestorResponse.ancestors || []),
        ...(ancestorResponse.item ? [ancestorResponse.item] : []),
      ]);
      setDraftName(response.space.name);
      setDraftDescription(response.space.description || '');
      setSearchResults(null);
      setSelectedIds(new Set());
    }).catch((err) => {
      if (!active) return;
      if (currentFolderId && err.status === 404) {
        deepLinkNoticeRef.current = 'This item is no longer available.';
        navigate(`/spaces/${spaceId}`, { replace: true });
        return;
      }
      setError('Could not load Space');
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currentFolderId, navigate, spaceId]);

  useEffect(() => {
    let active = true;
    const term = query.trim();
    if (!term) { setSearchResults(null); return () => { active = false; }; }
    if (typeof searchSpaceNodes !== 'function') return () => { active = false; };
    const timer = window.setTimeout(() => {
      searchSpaceNodes(spaceId, { query: term, withinNodeId: currentFolderId, scope: 'folder', limit: 100 })
        .then((response) => {
          if (!active) return;
          setSearchResults(response.items || []);
          setSearchTotal(response.total || 0);
        })
        .catch((err) => active && setError('Search failed'));
    }, 220);
    return () => { active = false; window.clearTimeout(timer); };
  }, [currentFolderId, query, spaceId]);

  const visible = useMemo(() => (searchResults || space?.items || []).filter((item) => {
    const typeMatch = filter === 'all' || item.mediaType === filter;
    return typeMatch;
  }), [filter, searchResults, space]);
  const expandedItem = [...(space?.items || []), ...(searchResults || [])].find((item) => item.id === expandedItemId) || null;

  const syncSummary = (next) => updateCachedSpace(next.id, {
    ...next,
    itemCount: next.itemCount ?? next.items?.length ?? 0,
    previewItems: next.previewItems || (next.items || []).slice(0, 4),
  });
  const appendItems = (items) => {
    const existing = new Set((space?.items || []).map((item) => item.id));
    const next = { ...space, items: [...items.filter((item) => !existing.has(item.id)), ...(space?.items || [])] };
    setSpace(next);
    syncSummary(next);
  };

  const reloadCurrentFolder = async () => {
    if (typeof getSpaceNodes !== 'function') return;
    const response = await getSpaceNodes(spaceId, { parentId: currentFolderId });
    setSpace((current) => ({ ...current, items: response.items || [] }));
    setNextCursor(response.nextCursor || null);
    setSelectedIds(new Set());
    setSearchResults(null);
  };

  const toggleSelected = (item) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
    return next;
  });

  const duplicateSelected = async () => {
    if (!selectedIds.size) return;
    try { await duplicateSpaceNodes(spaceId, [...selectedIds]); await reloadCurrentFolder(); }
    catch (err) { setError('Could not duplicate selection'); }
  };

  const deleteSelected = async () => {
    if (!selectedIds.size) return;
    let message = `Delete ${selectedIds.size} selected item${selectedIds.size === 1 ? '' : 's'} and any descendants? Space-owned text cannot be restored.`;
    if (typeof previewSpaceNodeDeletion === 'function') {
      try {
        const response = await previewSpaceNodeDeletion(spaceId, [...selectedIds]);
        const preview = response.preview || {};
        const counts = preview.counts || {};
        message = `Delete ${preview.totalCount || selectedIds.size} items (${counts.folder || 0} folders, ${counts.text || 0} text, ${counts.skill || 0} skills, ${counts.asset || 0} media, ${counts.character || 0} characters)?${preview.deletesOwnedText ? ' Space-owned text cannot be restored.' : ''}${preview.retainsLibraryEntities ? ' Library assets and characters will remain available.' : ''}`;
      } catch (err) { setError('Could not inspect selection'); return; }
    }
    if (!window.confirm(message)) return;
    try { await deleteSpaceNodes(spaceId, [...selectedIds]); await reloadCurrentFolder(); }
    catch (err) { setError('Could not delete selection'); }
  };

  const isFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    if (!space || uploadingFiles) return;
    dragDepth.current += 1;
    setDraggingFiles(true);
  };

  const handleDragOver = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = space && !uploadingFiles ? 'copy' : 'none';
  };

  const handleDragLeave = (event) => {
    if (!dragDepth.current) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDraggingFiles(false);
  };

  const handleDrop = async (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDraggingFiles(false);
    if (!space || uploadingFiles) return;

    const files = Array.from(event.dataTransfer?.files || []).filter((file) =>
      ['image/', 'video/', 'audio/'].some((prefix) => file.type.startsWith(prefix)) ||
      file.type.startsWith('text/') || SPACE_FILE_EXTENSION.test(file.name)
    );
    if (!files.length) {
      setError('Drop a supported text, image, video, or audio file to upload it.');
      return;
    }

    setUploadingFiles(true);
    setError(null);
    const uploaded = [];
    try {
      for (const file of files) {
        const response = currentFolderId ? await uploadSpaceItem(space.id, file, currentFolderId) : await uploadSpaceItem(space.id, file);
        if (response.item) uploaded.push(response.item);
        if (response.asset) addOrgAsset(response.asset);
      }
    } catch (err) {
      setError('Upload failed');
    } finally {
      if (uploaded.length) appendItems(uploaded);
      setUploadingFiles(false);
    }
  };

  const saveMetadata = async () => {
    const previous = space;
    const optimistic = { ...space, name: draftName.trim(), description: draftDescription.trim() || null };
    setSpace(optimistic); setEditing(false); syncSummary(optimistic);
    try {
      const response = await updateSpace(space.id, { name: optimistic.name, description: optimistic.description });
      const merged = { ...response.space, items: optimistic.items };
      setSpace(merged); syncSummary(merged);
    } catch (err) { setSpace(previous); syncSummary(previous); setError('Could not update Space'); }
  };

  const removeItem = async (item) => {
    let message = `Remove “${item.name}” from this Space?`;
    if (typeof previewSpaceNodeDeletion === 'function') {
      try {
        const response = await previewSpaceNodeDeletion(space.id, [item.id]);
        const preview = response.preview || {};
        if ((preview.totalCount || 1) > 1) message = `Delete “${item.name}” and ${preview.descendantCount} descendant${preview.descendantCount === 1 ? '' : 's'}? Space-owned text cannot be restored; library entities remain available.`;
      } catch (err) { setError('Could not inspect item'); return false; }
    }
    if (!window.confirm(message)) return false;
    try {
      if (typeof deleteSpaceNodes === 'function') {
        await deleteSpaceNodes(space.id, [item.id]);
        await reloadCurrentFolder();
      } else {
        await removeSpaceItem(space.id, item.id);
        setSpace((current) => ({ ...current, items: current.items.filter((entry) => entry.id !== item.id) }));
      }
      if (expandedItemId === item.id) setExpandedItemId(null);
      return true;
    } catch (err) {
      setError('Could not remove element');
      return false;
    }
  };

  const saveItemDescription = async (item, description) => {
    const previous = space;
    const nextDescription = description.trim() || null;
    const optimistic = {
      ...space,
      items: space.items.map((entry) => entry.id === item.id ? { ...entry, description: nextDescription } : entry),
    };
    setSpace(optimistic); syncSummary(optimistic); setError(null);
    try {
      const response = await updateSpaceItem(space.id, item.id, { description: nextDescription });
      const next = {
        ...optimistic,
        items: optimistic.items.map((entry) => entry.id === item.id ? response.item : entry),
      };
      setSpace(next); syncSummary(next);
      if (response.item.kind === 'asset') {
        updateOrgAssetInStore(response.item.sourceId, { prompt: response.item.description });
        patchOrgAssetDetail(response.item.sourceId, { prompt: response.item.description });
      } else if (response.item.kind === 'character') {
        updateCharacterInStore(response.item.sourceId, { personality: response.item.description });
        patchCharacterDetail(response.item.sourceId, { personality: response.item.description });
      }
    } catch (err) {
      setSpace(previous); syncSummary(previous); setError('Could not update description');
      throw err;
    }
  };

  const saveItemName = async (item, name) => {
    const previous = space;
    const optimistic = {
      ...space,
      items: space.items.map((entry) => entry.id === item.id ? { ...entry, name } : entry),
    };
    setSpace(optimistic); syncSummary(optimistic); setError(null);
    try {
      const response = await updateSpaceItem(space.id, item.id, { name });
      const next = {
        ...optimistic,
        items: optimistic.items.map((entry) => entry.id === item.id ? response.item : entry),
      };
      setSpace(next); syncSummary(next);
      if (response.item.kind === 'asset') {
        updateOrgAssetInStore(response.item.sourceId, { name: response.item.name });
        patchOrgAssetDetail(response.item.sourceId, { name: response.item.name });
      } else if (response.item.kind === 'character') {
        updateCharacterInStore(response.item.sourceId, { name: response.item.name });
        patchCharacterDetail(response.item.sourceId, { name: response.item.name });
      }
    } catch (err) {
      setSpace(previous); syncSummary(previous); setError('Could not rename element');
      throw err;
    }
  };

  const saveTextItem = async (item, updates) => {
    const previous = space;
    const optimisticItem = {
      ...item, ...updates,
      mediaType: updates.textFormat === 'skill' ? 'skill' : 'text',
      description: updates.content.slice(0, 240) || null,
    };
    const optimistic = { ...space, items: space.items.map((entry) => entry.id === item.id ? optimisticItem : entry) };
    setSpace(optimistic); syncSummary(optimistic); setError(null);
    try {
      const response = await updateSpaceItem(space.id, item.id, updates);
      const next = { ...optimistic, items: optimistic.items.map((entry) => entry.id === item.id ? response.item : entry) };
      setSpace(next); syncSummary(next); setExpandedItemId(response.item.id);
    } catch (err) {
      setSpace(previous); syncSummary(previous); setError('Could not save text item'); throw err;
    }
  };

  const openItem = (item) => {
    if (item.kind === 'folder' || item.mediaType === 'folder') {
      setBreadcrumbs((current) => [...current, item]);
      setSpace((current) => current ? { ...current, items: [] } : current);
      navigate(`/spaces/${spaceId}/folders/${item.id}`); setQuery(''); setFilter('all');
    } else setExpandedItemId(item.id);
  };

  const loadMoreItems = async () => {
    if (loadingMore) return;
    setLoadingMore(true); setError(null);
    try {
      if (searchResults) {
        const response = await searchSpaceNodes(spaceId, { query: query.trim(), withinNodeId: currentFolderId, scope: 'folder', offset: searchResults.length, limit: 100 });
        setSearchResults((current) => [...(current || []), ...(response.items || [])]);
        setSearchTotal(response.total || 0);
      } else if (nextCursor) {
        const response = await getSpaceNodes(spaceId, { parentId: currentFolderId, cursor: nextCursor });
        setSpace((current) => ({ ...current, items: [...(current.items || []), ...(response.items || [])] }));
        setNextCursor(response.nextCursor || null);
      }
    } catch (err) { setError('Could not load more items'); }
    finally { setLoadingMore(false); }
  };

  const destroy = async () => {
    if (!window.confirm('Delete this Space? Its library elements will remain available.')) return;
    try { await deleteSpace(space.id); removeCachedSpace(space.id); navigate('/spaces'); }
    catch (err) { setError('Could not delete Space'); }
  };

  return (
    <AuthenticatedLayout>
      <main
        className={`spaces-page space-detail-page${draggingFiles ? ' is-file-dragging' : ''}`}
        data-testid="space-drop-target"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {(draggingFiles || uploadingFiles) && (
          <div className="space-drop-overlay" aria-live="polite">
            {uploadingFiles ? <LoaderCircle className="spaces-spin" size={28} /> : <Upload size={28} />}
            <strong>{uploadingFiles ? 'Uploading media…' : 'Drop to add to this Space'}</strong>
            {!uploadingFiles && <span>Text, images, videos, and audio</span>}
          </div>
        )}
        <button className="spaces-back" onClick={() => navigate('/spaces')}><ArrowLeft size={15} /> Spaces</button>
        {loading ? <div className="spaces-center"><LoaderCircle className="spaces-spin" /> Loading Space…</div> : error && !space ? <div className="spaces-error">{error}</div> : space && <>
          <header className="space-detail-header">
            <div className="space-detail-copy">
              {editing ? <><input className="space-title-input" value={draftName} onChange={(e) => setDraftName(e.target.value)} autoFocus /><textarea className="space-description-input" value={draftDescription} onChange={(e) => setDraftDescription(e.target.value)} placeholder="Add a description…" /><div className="space-edit-actions"><button className="spaces-button secondary" onClick={() => { setEditing(false); setDraftName(space.name); setDraftDescription(space.description || ''); }}>Cancel</button><button className="spaces-button primary" disabled={!draftName.trim()} onClick={saveMetadata}>Save</button></div></> : <button className="space-title-button" onClick={() => setEditing(true)}><h1>{space.name}</h1><p>{space.description || 'Add a description'}</p></button>}
            </div>
            <div className="space-detail-actions"><button className="spaces-button secondary" onClick={() => setNodeDialogType('folder')}><Folder size={16} /> New folder</button><button className="spaces-button secondary" onClick={() => setNodeDialogType('text')}><FileText size={16} /> New text</button><button className="spaces-button secondary" onClick={() => setNodeDialogType('skill')}><FileCode2 size={16} /> New skill</button><button className="spaces-button primary" onClick={() => setPickerOpen(true)}><Plus size={16} /> Add elements</button><button className="spaces-icon-button danger" onClick={destroy} aria-label="Delete Space"><Trash2 size={16} /></button></div>
          </header>
          {error && <div className="spaces-error dismissible">{error}<button onClick={() => setError(null)}><X size={14} /></button></div>}
          <div className="spaces-toolbar detail"><label className="spaces-search"><Search size={15} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this folder…" /></label>{selectedIds.size ? <div className="space-bulk-actions"><span>{selectedIds.size} selected</span><button className="spaces-button secondary" onClick={() => setMoveOpen(true)}>Move</button><button className="spaces-button secondary" onClick={duplicateSelected}>Duplicate</button><button className="spaces-button danger" onClick={deleteSelected}>Delete</button></div> : <span>{space.items.length} items</span>}</div>
          <nav className="space-breadcrumbs" aria-label="Folder path"><button onClick={() => navigate(`/spaces/${spaceId}`)}>{space.name}</button>{breadcrumbs.map((folder) => <span key={folder.id}><ChevronRight size={14} /><button onClick={() => navigate(`/spaces/${spaceId}/folders/${folder.id}`)}>{folder.name}</button></span>)}</nav>
          <div className="spaces-filter-row detail" role="tablist">{FILTERS.map(([value, label]) => <button key={value} role="tab" aria-selected={filter === value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}</button>)}</div>
          {visible.length ? <div className="space-items-grid">{visible.map((item) => <SpaceItemCard key={item.id} item={item} onOpen={openItem} onRemove={removeItem} selected={selectedIds.has(item.id)} onToggle={toggleSelected} />)}</div> : <div className="spaces-empty compact"><FolderOpen size={26} /><h2>{space.items.length ? 'No matching elements here' : 'This Space is empty'}</h2><p>{space.items.length ? 'Try another filter, search, or folder.' : 'Add folders, text, library elements, or upload media.'}</p>{!space.items.length && <button className="spaces-button primary" onClick={() => setPickerOpen(true)}>Add elements</button>}</div>}
          {(searchResults ? searchResults.length < searchTotal : Boolean(nextCursor)) && <div className="spaces-load-more"><button className="spaces-button secondary" disabled={loadingMore} onClick={loadMoreItems}>{loadingMore ? 'Loading…' : 'Load more'}</button></div>}
        </>}
      </main>
      {pickerOpen && <SpaceItemPicker spaceId={spaceId} parentId={currentFolderId} onClose={() => setPickerOpen(false)} onAdded={appendItems} onUploaded={appendItems} />}
      {nodeDialogType && <CreateSpaceNodeDialog open spaceId={spaceId} parentId={currentFolderId} initialType={nodeDialogType} onClose={() => setNodeDialogType(null)} onCreated={(item) => appendItems([item])} />}
      <SpaceMoveDialog open={moveOpen} spaceId={spaceId} selectedIds={[...selectedIds]} onClose={() => setMoveOpen(false)} onMoved={reloadCurrentFolder} />
      {expandedItem && expandedItem.kind === 'text' ? <TextSpaceItemModal item={expandedItem} onClose={() => setExpandedItemId(null)} onSaved={saveTextItem} onRemove={removeItem} /> : expandedItem && <ExpandedSpaceItemModal item={expandedItem} onClose={() => setExpandedItemId(null)} onDescriptionSaved={saveItemDescription} onNameSaved={saveItemName} onRemove={removeItem} />}
    </AuthenticatedLayout>
  );
}
